import React, { useEffect, useState } from "react";
import { api, fmtQty } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { POStatus } from "@/components/purchasing/POStatus";

const Info = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
    <div className="font-medium text-slate-800">{value || "-"}</div>
  </div>
);

// Full trace of a PO: ordered vs received per line, plus every goods receipt posted against it.
export const POTraceDialog = ({ poId, onClose }) => {
  const { t } = useLang();
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    if (poId) api.get(`/pos/${poId}/trace`).then((r) => setData(r.data));
  }, [poId]);

  return (
    <Dialog open={!!poId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="po-trace-dialog">
        <DialogHeader><DialogTitle>{t("po_trace")} · <span className="font-mono">{data?.po_number}</span></DialogTitle></DialogHeader>
        {data && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
              <Info label={t("vendor")} value={data.vendor} />
              <Info label={t("order_date")} value={(data.order_date || "").slice(0, 10)} />
              <Info label={t("expected_date")} value={(data.expected_date || "").slice(0, 10)} />
              <Info label={t("status")} value={<POStatus status={data.status} />} />
              <Info label={t("receipts")} value={`${new Set(data.receipts.map((r) => r.doc_no)).size}x`} />
            </div>
            <table className="min-w-full text-sm border border-slate-200" data-testid="po-trace-lines">
              <thead className="bg-slate-100/80 text-slate-700">
                <tr>
                  <th className="text-left py-2 px-3">{t("item")}</th>
                  <th className="text-right py-2 px-3">{t("qty")} PO</th>
                  <th className="text-right py-2 px-3">{t("received")}</th>
                  <th className="text-right py-2 px-3">{t("remaining")}</th>
                  <th className="py-2 px-3 w-40">%</th>
                </tr>
              </thead>
              <tbody>
                {data.lines.map((l) => {
                  const pct = Math.min(100, (l.received_qty / l.qty) * 100);
                  return (
                    <tr key={l.line_id} className="border-t border-slate-100">
                      <td className="py-2 px-3"><span className="font-mono text-xs text-slate-500">{l.item_code}</span> {l.item_name}</td>
                      <td className="py-2 px-3 text-right font-mono">{fmtQty(l.qty)}</td>
                      <td className="py-2 px-3 text-right font-mono text-emerald-700">{fmtQty(l.received_qty)}</td>
                      <td className="py-2 px-3 text-right font-mono text-amber-700">{fmtQty(l.remaining_qty)}</td>
                      <td className="py-2 px-3">
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-emerald-600" style={{ width: `${pct}%` }} /></div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">{t("receipts")}</div>
              <table className="min-w-full text-sm border border-slate-200" data-testid="po-trace-receipts">
                <thead className="bg-slate-100/80 text-slate-700">
                  <tr>
                    <th className="text-left py-2 px-3">{t("date")}</th>
                    <th className="text-left py-2 px-3">{t("doc_no")}</th>
                    <th className="text-left py-2 px-3">{t("item")}</th>
                    <th className="text-right py-2 px-3">{t("qty")}</th>
                    <th className="text-left py-2 px-3">{t("warehouse")}</th>
                    <th className="text-left py-2 px-3">{t("user")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.receipts.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100">
                      <td className="py-2 px-3 font-mono text-xs">{r.date}</td>
                      <td className="py-2 px-3 font-mono text-xs text-slate-500">{r.doc_no}</td>
                      <td className="py-2 px-3"><span className="font-mono text-xs text-slate-500">{r.item_code}</span> {r.item_name}</td>
                      <td className="py-2 px-3 text-right font-mono text-emerald-700">+{fmtQty(r.qty)}</td>
                      <td className="py-2 px-3">{t(`wh_${r.warehouse}`)}</td>
                      <td className="py-2 px-3 text-xs text-slate-500">{r.user}</td>
                    </tr>
                  ))}
                  {data.receipts.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-400">{t("no_receipts")}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
